import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import { games } from '../data/games';

/* ── Animations ─────────────────────────────── */
const scanline = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  10%  { opacity: 0.5; }
  90%  { opacity: 0.5; }
  100% { transform: translateY(400%); opacity: 0; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0,163,255,0.1); }
  50%       { box-shadow: 0 0 50px rgba(0,163,255,0.3); }
`;

/* ── Styled ──────────────────────────────────── */
const Section = styled.section`
  padding: 140px 80px;
  background: linear-gradient(to bottom, ${({ theme }) => theme.colors.mid}, ${({ theme }) => theme.colors.darker});
  overflow: hidden;

  @media (max-width: 900px) { padding: 80px 24px; }
`;

const Inner = styled.div`
  max-width: 1360px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 70px;
  flex-wrap: wrap;
  gap: 24px;
`;

const TitleBlock = styled.div``;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 8vw, 100px);
  line-height: 0.9;
  letter-spacing: 0.02em;

  .accent {
    background: ${({ theme }) => theme.gradients.brand};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const ViewAll = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 11px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.blue};
  text-decoration: none;
  padding: 10px 24px;
  border: 1px solid rgba(0,163,255,0.25);
  clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%);
  transition: all 0.3s;
  white-space: nowrap;

  &:hover {
    background: rgba(0,163,255,0.08);
    border-color: rgba(0,163,255,0.5);
  }

  span { transition: transform 0.3s; }
  &:hover span { transform: translateX(5px); }
`;

/* Featured two-column layout */
const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 3px;
  margin-bottom: 3px;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const SmallGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3px;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const GameCard = styled.div`
  position: relative;
  overflow: hidden;
  background: ${({ $bg }) => $bg || '#0a0a20'};
  min-height: ${({ $large }) => $large ? '520px' : '280px'};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 36px 32px;
  @media (max-width: 480px) { padding: 24px 18px; }
  
  transition: all 0.4s;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, transparent 100%);
    z-index: 1;
  }

  /* Scanline effect on hover */
  &::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 3px;
    background: linear-gradient(to right, transparent, ${({ $accent }) => $accent || '#00A3FF'}, transparent);
    opacity: 0;
    z-index: 3;
    animation: ${scanline} 2.5s linear infinite;
    animation-play-state: paused;
  }

  &:hover {
    &::after { animation-play-state: running; opacity: 1; }
  }
`;

const CardBgEmoji = styled.div`
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -60%);
  font-size: ${({ $large }) => $large ? '180px' : '100px'};
  opacity: 0.07;
  filter: grayscale(1);
  pointer-events: none;
  transition: transform 0.5s, opacity 0.4s;
  z-index: 0;

  ${GameCard}:hover & {
    transform: translate(-50%, -65%) scale(1.08);
    opacity: 0.12;
  }
`;

const CardGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 60% 40% at 50% 80%, ${({ $accent }) => $accent}22 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.4s;
  z-index: 0;

  ${GameCard}:hover & { opacity: 1; }
`;

const CardBorderAccent = styled.div`
  position: absolute;
  bottom: 0; left: 0;
  height: 2px; width: 0%;
  background: ${({ $accent }) => $accent || '#00A3FF'};
  transition: width 0.5s ease;
  z-index: 4;
  box-shadow: 0 0 10px ${({ $accent }) => $accent || '#00A3FF'};

  ${GameCard}:hover & { width: 100%; }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 2;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0,0,0,0.6);
  border: 1px solid ${({ $color }) => $color}44;
  padding: 4px 12px;
  margin-bottom: 14px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9px;
  letter-spacing: 0.28em;
  color: ${({ $color }) => $color};
  text-transform: uppercase;

  &::before {
    content: '';
    width: 5px; height: 5px;
    background: ${({ $color }) => $color};
    border-radius: 50%;
    display: inline-block;
    animation: blink 2s infinite;
  }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

const GameTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ $large }) => $large ? '62px' : '38px'};
  line-height: 0.9;
  letter-spacing: 0.03em;
  color: #fff;
  margin-bottom: 12px;
  transition: color 0.3s;

  ${GameCard}:hover & {
    background: ${({ $accent }) => `linear-gradient(135deg, #fff, ${$accent})`};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 600px) { font-size: ${({ $large }) => $large ? '44px' : '30px'}; }
`;

const GameGenre = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.15em;
  color: rgba(255,255,255,0.82);
  text-transform: uppercase;
  margin-bottom: 16px;
`;

const GameDesc = styled.p`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 14px;
  line-height: 1.7;
  color: rgba(255,255,255,0.75);
  margin-bottom: 22px;
  display: -webkit-box;
  -webkit-line-clamp: ${({ $large }) => $large ? 3 : 2};
  -webkit-box-clamp: ${({ $large }) => $large ? 3 : 2};
  overflow: hidden;
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9px;
  letter-spacing: 0.2em;
  color: rgba(255,255,255,0.78);
  padding: 3px 10px;
  text-transform: uppercase;
  transition: all 0.3s;

  ${GameCard}:hover & {
    border-color: ${({ $accent }) => $accent}44;
    color: ${({ $accent }) => $accent}aa;
  }
`;

const ExploreBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10px;
  letter-spacing: 0.25em;
  color: ${({ $accent }) => $accent || '#00A3FF'};
  text-decoration: none;
  text-transform: uppercase;
  opacity: 0;
  transform: translateY(8px);
  transition: all 0.3s;

  ${GameCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }

  span { transition: transform 0.3s; }
  &:hover span { transform: translateX(5px); }
`;

const PlatformDots = styled.div`
  position: absolute;
  top: 20px; right: 20px;
  display: flex;
  gap: 6px;
  z-index: 2;
`;

const Platform = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 8px;
  letter-spacing: 0.15em;
  color: rgba(255,255,255,0.78);
  padding: 3px 8px;
  text-transform: uppercase;
  border: 1px solid rgba(255,255,255,0.08);
`;

export default function GamesShowcase() {
  const { t } = useTranslation();
  const featured = games.filter(g => g.featured && !g.webGame);
  const others = games.filter(g => !g.featured && !g.webGame);

  return (
    <Section id="jogos">
      <Inner>
        <AnimatedSection>
          <Header>
            <TitleBlock>
              <SectionLabel>{t('gamesShowcase.sectionLabel')}</SectionLabel>
              <Title>
                {t('gamesShowcase.titlePrefix')} <span className="accent">{t('gamesShowcase.titleAccent')}</span>
              </Title>
            </TitleBlock>
            <ViewAll to="/games">
              {t('gamesShowcase.viewAll')} <span>→</span>
            </ViewAll>
          </Header>
        </AnimatedSection>

        <AnimatedSection>
          <FeaturedGrid>
            {featured.map((game, i) => (
              <GameCard
                key={game.id}
                $bg={game.gradient}
                $accent={game.accentColor}
                $large={i === 0}
                as={Link}
                to={game.webGame ? game.route : `/games/${game.id}`}

              >
                <CardBgEmoji $large={i === 0}>{game.emoji}</CardBgEmoji>
                <CardGlow $accent={game.accentColor} />
                <CardBorderAccent $accent={game.accentColor} />
                <PlatformDots>
                  {game.platforms.map(p => <Platform key={p}>{p}</Platform>)}
                </PlatformDots>
                <CardContent>
                  <StatusBadge $color={game.statusColor}>{game.status}</StatusBadge>
                  <GameTitle $large={i === 0} $accent={game.accentColor}>{game.title}</GameTitle>
                  <GameGenre>{game.genre}</GameGenre>
                  <GameDesc $large={i === 0}>{game.description}</GameDesc>
                  <TagsRow>
                    {game.tags.map(tag => <Tag key={tag} $accent={game.accentColor}>{tag}</Tag>)}
                  </TagsRow>
                  <ExploreBtn to={game.webGame ? game.route : `/games/${game.id}`} $accent={game.accentColor}>
                    {t('gamesShowcase.exploreUniverse')} <span>→</span>
                  </ExploreBtn>
                </CardContent>
              </GameCard>
            ))}
          </FeaturedGrid>

          <SmallGrid>
            {others.map(game => (
              <GameCard
                key={game.id}
                $bg={game.gradient}
                $accent={game.accentColor}
                as={Link}
                to={game.webGame ? game.route : `/games/${game.id}`}

              >
                <CardBgEmoji>{game.emoji}</CardBgEmoji>
                <CardGlow $accent={game.accentColor} />
                <CardBorderAccent $accent={game.accentColor} />
                <PlatformDots>
                  {game.platforms.map(p => <Platform key={p}>{p}</Platform>)}
                </PlatformDots>
                <CardContent>
                  <StatusBadge $color={game.statusColor}>{game.status}</StatusBadge>
                  <GameTitle $accent={game.accentColor}>{game.title}</GameTitle>
                  <GameGenre>{game.genre}</GameGenre>
                  <GameDesc>{game.description}</GameDesc>
                  <TagsRow>
                    {game.tags.slice(0, 3).map(tag => <Tag key={tag} $accent={game.accentColor}>{tag}</Tag>)}
                  </TagsRow>
                  <ExploreBtn to={game.webGame ? game.route : `/games/${game.id}`} $accent={game.accentColor}>
                    {t('gamesShowcase.explore')} <span>→</span>
                  </ExploreBtn>
                </CardContent>
              </GameCard>
            ))}
          </SmallGrid>
        </AnimatedSection>
      </Inner>
    </Section>
  );
}
